<template>
    <header :class="[
        'sticky top-0 z-50 border-b transition-colors',
        scrolled ? 'border-gray-100 bg-white shadow-sm' : 'border-transparent bg-white/90 backdrop-blur-lg'
    ]">
        <nav class="mx-auto flex max-w-7xl items-center justify-between px-6 py-3 lg:px-8" aria-label="Global">
            <router-link to="/" class="flex shrink-0 items-center">
                <img class="h-14 w-auto object-contain" src="/images/logo.png" alt="logo" />
            </router-link>

            <div class="hidden lg:flex lg:items-center lg:gap-x-1">
                <template v-for="item in navigation" :key="item.name">
                    <router-link v-if="!item.dropdown" :to="item.href" :class="[
                        item.current ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-primary-700',
                        'rounded-full px-4 py-2 text-sm font-semibold transition-colors'
                    ]">
                        {{ item.name }}
                    </router-link>

                    <Menu v-else as="div" class="relative">
                        <MenuButton
                            class="inline-flex items-center gap-x-1 rounded-full px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 hover:text-primary-700">
                            {{ item.name }}
                            <ChevronDownIcon class="size-4 text-gray-400" aria-hidden="true" />
                        </MenuButton>
                        <Transition enter="transition ease-out duration-100" enter-from="transform opacity-0 scale-95"
                            enter-to="transform opacity-100 scale-100" leave="transition ease-in duration-75"
                            leave-from="transform opacity-100 scale-100" leave-to="transform opacity-0 scale-95">
                            <MenuItems
                                class="absolute left-0 z-50 mt-2 w-64 origin-top-left rounded-2xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                                <MenuItem v-for="subItem in item.dropdown.items" :key="subItem.name" v-slot="{ active }">
                                    <router-link :to="subItem.href"
                                        :class="[active ? 'bg-gray-50 text-primary-700' : 'text-gray-700', 'block rounded-xl px-3 py-2 text-sm font-medium']">
                                        {{ subItem.name }}
                                    </router-link>
                                </MenuItem>
                                <p v-if="!item.dropdown.items.length" class="px-3 py-2 text-sm text-gray-400">
                                    No programs available yet.
                                </p>
                            </MenuItems>
                        </Transition>
                    </Menu>
                </template>
            </div>

            <div class="hidden lg:flex lg:items-center lg:gap-x-3">
                <Menu v-if="user" as="div" class="relative">
                    <MenuButton
                        class="flex items-center gap-x-2 rounded-full py-1 pl-1 pr-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden">
                        <img class="size-8 rounded-full object-cover" :src="user.avatar || '/images/default-avatar.png'"
                            alt="User Avatar" />
                        <ChevronDownIcon class="size-4 text-gray-400" aria-hidden="true" />
                    </MenuButton>
                    <Transition enter="transition ease-out duration-100" enter-from="transform opacity-0 scale-95"
                        enter-to="transform opacity-100 scale-100" leave="transition ease-in duration-75"
                        leave-from="transform opacity-100 scale-100" leave-to="transform opacity-0 scale-95">
                        <MenuItems
                            class="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl bg-white p-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                            <MenuItem v-slot="{ active }"
                                v-for="item in userDropdownItems.filter(i => i.roles.length === 0 || i.roles.some(role => user.roles.includes(role)))"
                                :key="item.name">
                                <a :href="item.href" @click="item.name === 'Logout' ? handleLogout($event) : null" :class="[
                                    active ? 'bg-gray-50 text-primary-700' : 'text-gray-700',
                                    'group flex w-full items-center gap-x-2 rounded-xl px-3 py-2 text-sm font-medium'
                                ]">
                                    <component :is="item.icon" class="size-5 text-gray-400" />
                                    <span>{{ item.name }}</span>
                                </a>
                            </MenuItem>
                        </MenuItems>
                    </Transition>
                </Menu>
                <template v-else>
                    <router-link to="/login">
                        <Button variant="ghost" size="sm">Login</Button>
                    </router-link>
                    <router-link to="/register">
                        <Button variant="primary" size="sm">Register</Button>
                    </router-link>
                </template>
            </div>

            <button type="button" class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 lg:hidden"
                @click="mobileMenuOpen = true">
                <span class="sr-only">Open main menu</span>
                <Bars3Icon class="size-6" aria-hidden="true" />
            </button>
        </nav>

        <TransitionRoot as="template" :show="mobileMenuOpen">
            <Dialog class="lg:hidden" @close="mobileMenuOpen = false">
                <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0"
                    enter-to="opacity-100" leave="transition-opacity ease-linear duration-200" leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="fixed inset-0 z-50 bg-gray-900/30" />
                </TransitionChild>

                <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
                    enter-from="translate-x-full" enter-to="translate-x-0"
                    leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
                    leave-to="translate-x-full">
                    <DialogPanel
                        class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                        <div class="flex items-center justify-between">
                            <router-link to="/" class="flex shrink-0 items-center" @click="mobileMenuOpen = false">
                                <img class="h-10 w-auto object-contain" src="/images/logo.png" alt="logo" />
                            </router-link>
                            <button type="button" class="-m-2.5 rounded-md p-2.5 text-gray-700" @click="mobileMenuOpen = false">
                                <span class="sr-only">Close menu</span>
                                <XMarkIcon class="size-6" aria-hidden="true" />
                            </button>
                        </div>
                        <div class="mt-6 flow-root">
                            <div class="-my-6 divide-y divide-gray-100">
                                <div class="space-y-1 py-6">
                                    <template v-for="item in navigation" :key="item.name">
                                        <router-link v-if="!item.dropdown" :to="item.href"
                                            class="block rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
                                            @click="mobileMenuOpen = false">
                                            {{ item.name }}
                                        </router-link>
                                        <div v-else class="px-3 py-2.5">
                                            <p class="text-base font-semibold text-gray-900">{{ item.name }}</p>
                                            <div class="mt-2 space-y-1 border-l border-gray-100 pl-3">
                                                <router-link v-for="subItem in item.dropdown.items" :key="subItem.name"
                                                    :to="subItem.href" class="block py-1.5 text-sm font-medium text-gray-600 hover:text-primary-700"
                                                    @click="mobileMenuOpen = false">
                                                    {{ subItem.name }}
                                                </router-link>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <div class="space-y-1 py-6">
                                    <template v-if="user">
                                        <a v-for="item in userDropdownItems.filter(i => i.roles.length === 0 || i.roles.some(role => user.roles.includes(role)))"
                                            :key="item.name" :href="item.href"
                                            class="flex items-center gap-x-2 rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
                                            @click="item.name === 'Logout' ? handleLogout($event) : (mobileMenuOpen = false)">
                                            <component :is="item.icon" class="size-5 text-gray-400" />
                                            {{ item.name }}
                                        </a>
                                    </template>
                                    <template v-else>
                                        <router-link to="/login"
                                            class="block rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
                                            @click="mobileMenuOpen = false">
                                            Login
                                        </router-link>
                                        <router-link to="/register"
                                            class="block rounded-lg px-3 py-2.5 text-base font-semibold text-primary-700 hover:bg-primary-50"
                                            @click="mobileMenuOpen = false">
                                            Register
                                        </router-link>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </TransitionRoot>
    </header>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionRoot, TransitionChild, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import {
    Bars3Icon,
    XMarkIcon,
    AcademicCapIcon,
    UserIcon,
    ArrowLeftEndOnRectangleIcon
} from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import Button from '@/components/ui/Button.vue';
import { useAuth } from '@/composables/useAuth';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getFeaturedCategories } from '@/services/categoryService';

const { getUser, logout } = useAuth();
const router = useRouter();
const user = ref(null);
const mobileMenuOpen = ref(false);
const scrolled = ref(false);

const updateScrolled = () => {
    scrolled.value = window.scrollY > 8;
};

const navigation = [
    {
        name: 'Programs', href: '#', current: false, dropdown: {
            items: []
        }
    },
    { name: 'Books', href: '/books', current: false, dropdown: false },
    { name: 'About Us', href: '/about-us', current: false, dropdown: false },
    { name: 'Testimonial', href: '/#testimonials', current: false, dropdown: false },
    { name: 'Contact Us', href: '/contact-us', current: false, dropdown: false },
    { name: 'Privacy Policy', href: '/privacy-policy', current: false, dropdown: false },
];

const userDropdownItems = [
    { name: 'Account', href: '/account', icon: UserIcon, roles: [] },
    { name: 'Learning', href: '/learning', icon: AcademicCapIcon, roles: ['student'] },
    { name: 'Dashboard', href: '/dashboard', icon: AcademicCapIcon, roles: ['teacher'] },
    { name: 'Logout', href: '/logout', icon: ArrowLeftEndOnRectangleIcon, roles: [] },
];

const handleLogout = async (event) => {
    event.preventDefault();
    mobileMenuOpen.value = false;
    await logout();
    user.value = null;
    router.push('/');
};

onMounted(() => {
    getUser()
        .then(userData => {
            user.value = userData;
        })
        .catch(error => {
            // console.error('Error fetching user data:', error);
        });

    getFeaturedCategories().then(data => {
        const categories = data.data;

        navigation[0].dropdown.items = categories.map(category => ({
            name: category.name,
            href: `/courses?category=${category.slug}`
        }));
    });

    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
})

onUnmounted(() => {
    window.removeEventListener('scroll', updateScrolled);
})
</script>
